<template>
  <div class="staff-request-detail">
    <LoadSpinner v-if="loading" style="padding: 2rem 0" />

    <div v-if="request && employee" class="container">
      <div class="sidebar">
        <StipendUserCard :employee="employee" />
        <br />
        <label>Administrator Notes:</label>
        <md-field class="admin-notes-container">
          <md-textarea
            id="admin-notes-text"
            :disabled="isReadOnlyAdmin()"
            v-model="request.adminNotes"
          ></md-textarea>
        </md-field>
        <div v-if="!isReadOnlyAdmin()" class="admin-notes-container__btn-container">
          <md-button class="md-raised" @click="onSaveAdminNotes" :disabled="updatingNotes">
            Save
          </md-button>
          <p class="text-small text-upper">
            Last updated: {{ request.adminNotesLastUpdated | formatDateShort }}
          </p>
        </div>
        <div v-if="user.isAdmin || user.isStaff" style="padding: 0.9rem 0 0 0">
          <md-datepicker
            :md-clearable="isEditableAdmin"
            v-model="request.followUpDate"
            md-immediately
            :md-disabled-dates="
              !isEditableAdmin
                ? () => {
                    return true
                  }
                : () => {
                    return false
                  }
            "
          >
          <label>Follow-up date</label>
          </md-datepicker>
        </div>
      </div>

      <div class="request-content">
        <div class="request-content__section">
          <h5 class="text-blue text-upper text-small">Stipend Request Detail</h5>
          <div v-if="request.isEditing" class="editing-alert">
            <md-icon class="editing-alert__icon">warning</md-icon>
            <p>User is currently editing request</p>
          </div>
          <div class="title-block">
            <h2 class="title">Stipend Request Submitted on {{ request.submittedOn | formatDateShort }}</h2>
            <md-button
              v-if="showReferButton"
              class="md-raised"
              :disabled="request.isReferred || referring"
              @click="onReferRequest"
            >
              <p v-if="!referring">{{ request.isReferred ? 'Referred' : 'Refer' }}</p>
              <LoadSpinner v-else />
            </md-button>
          </div>
          <p>
            <strong>Total Requested: {{ request.amount | currency }}</strong>
          </p>
        </div>

        <!-- Status Section -->
        <div class="request-content__section" v-if="request && request.isClosed">
          <p><strong>Status:</strong> {{ request.status }}</p>
          <p><strong>Payout type:</strong> {{ request.payoutType }}</p>
        </div>

        <div class="request-content__section" v-if="!request.isClosed">
          <div class="request-content__section-top">
            <div v-if="isReadOnlyAdmin()">
              <md-field>
                <label for="status">Status</label>
                <md-input :value="getStatus(editableRequest.status)" :disabled="true"> </md-input>
              </md-field>
            </div>
            <div v-if="!isReadOnlyAdmin()">
              <md-field>
                <label for="payoutType">Payout Type</label>
                <md-select
                  v-model="editableRequest.payoutType"
                  name="payoutType"
                  id="payoutType"
                  :disabled="updating"
                >
                  <md-option
                    v-for="payoutType in StipendRequest.options.PAYOUT_TYPE_CHOICES"
                    :value="payoutType.value"
                    :key="payoutType.value"
                    :disabled="updating"
                  >
                    {{ payoutType.label }}
                  </md-option>
                </md-select>
              </md-field>
              <md-field>
                <label for="status">Status</label>
                <md-select
                  v-model="editableRequest.status"
                  name="status"
                  id="status"
                  :disabled="updating"
                >
                  <md-option
                    v-for="statusChoice in availableStatusChoices"
                    :value="statusChoice.value"
                    :key="statusChoice.value"
                    :disabled="updating"
                  >
                    {{ statusChoice.label }}
                  </md-option>
                </md-select>
              </md-field>

              <div>
                <md-field>
                  <md-icon>attach_money</md-icon>
                  <label>Amount to Disburse</label>
                  <md-input
                    type="number"
                    v-model="editableRequest.amount"
                    :disabled="updating"
                  ></md-input>
                </md-field>
              </div>

              <div>
                <md-checkbox
                  v-model="editableRequest.isAtRisk"
                  :disabled="updating"
                  class="at-risk"
                >Flag as "At Risk"?</md-checkbox>
              </div>
            </div>

            <div v-if="!isReadOnlyAdmin()">
              <md-field>
                <label for="assigned">Assigned Staff</label>
                <md-select
                  name="assigned"
                  id="assigned"
                  v-model="request.assignedTo"
                  @md-selected="onUpdateAssigned"
                >
                  <md-option v-for="staff in staff.list" :value="staff.id" :key="staff.id">
                    {{ staff.fullName }}
                  </md-option>
                </md-select>
                <div @click="onResetAssigned" class="icon-container" v-if="request.assignedTo">
                  <md-icon style="color: gray">close</md-icon>
                </div>
              </md-field>
            </div>
          </div>

          <div v-if="editableRequest.status === StipendRequest.options.DECLINED">
            <md-field>
              <label for="declined-reason">Declined Reason</label>
              <md-select
                v-model="editableRequest.declinedReason"
                name="declined-reason"
                id="declined-reason"
                :disabled="updating"
              >
                <md-option
                  v-for="reasonDeclined in StipendRequest.options.REASON_DECLINED_CHOICES"
                  :value="reasonDeclined.value"
                  :key="reasonDeclined.value"
                >
                  {{ reasonDeclined.label }}
                </md-option>
              </md-select>
            </md-field>
          </div>

          <div v-if="editableRequest.status === StipendRequest.options.DECLINED">
            <md-field>
              <label for="declined-reason">Declined Reason Detail</label>
              <md-select
                v-model="editableRequest.declinedReasonDetail"
                name="declined-reason-detail"
                id="declined-reason-detail"
                :disabled="updating"
              >
                <md-option
                  v-for="reasonDeclinedDetail in StipendRequest.options.REASON_DECLINED_DETAIL_CHOICES"
                  :value="reasonDeclinedDetail.value"
                  :key="reasonDeclinedDetail.value"
                >
                  {{ reasonDeclinedDetail.label }}
                </md-option>
              </md-select>
            </md-field>
          </div>

          <div v-if="editableRequest.hasEdit">
            <md-button
              class="md-raised md-primary"
              :disabled="updating || isReadOnlyAdmin()"
              @click="updateStatus"
              >Decision</md-button
            >
          </div>
        </div>
        <!-- End Status Section -->

        <div class="request-content__section request-content__section--bordered">
          <div style="display: flex">
            <md-button
              @click="() => (mode = EVENT_DETAIL_MODE)"
              :class="{ 'md-primary': mode === EVENT_DETAIL_MODE }"
              style="margin-left: 0"
            >
              <i
                class="text-bold text-upper text-small"
                :class="{ 'text-blue': mode !== EVENT_DETAIL_MODE }"
              >
                Request Details
              </i>
            </md-button>
            <md-button
              v-if="showMessages"
              @click="() => (mode = MESSAGE_MODE)"
              :class="{ 'md-primary': mode === MESSAGE_MODE }"
            >
              <i
                class="text-bold text-upper text-small"
                :class="{ 'text-blue': mode !== MESSAGE_MODE }"
              >
                {{ communicationName }}
              </i>
            </md-button>
          </div>
        </div>

        <div class="request-content__section" v-if="mode === MESSAGE_MODE && !isReadOnlyAdmin()">
          <MessageForm :user="request.userRef" :stipendRequest="request" requestType="stipendRequest" @created="onMessageCreated" :staff-view="true" />
        </div>

        <div class="request-content__section" v-if="mode === MESSAGE_MODE">
          <MessagesList
            :messages="messages"
            @next-page-loaded="markAllMessagesRead"
            @message-deleted="refreshMessages"
            :staff-view="true"
          />
        </div>

        <!-- Surveys Section -->

        <!-- Impact Statement -->
        <div v-if="mode === EVENT_DETAIL_MODE">
          <div
            v-if="request.impactStatementResponse"
            class="card stipend-request fit-content"
          >
            <div class="card__section">
              <h2>Impact Statement</h2>
              <LoadSpinner v-if="request.loading" />
              <template v-else>
                <template v-for="question in request.impactStatementResponse.questions">
                  <QuestionAnswer :question="question" />
                </template>
              </template>
            </div>
          </div>
          <div
            v-if="!request.impactStatementResponse && request.programMonth == 1"
            class="card stipend-request fit-content error-card"
          >
            <div class="card__section">
              <h2>Impact Statement</h2>
              <p>No Impact Statement survey submitted.</p>
            </div>
          </div>

          <!-- Personal Details -->
          <div
            v-if="request.personalDetailsResponse"
            class="card stipend-request fit-content"
          >
            <div class="card__section">
              <h2>Personal Details</h2>
              <LoadSpinner v-if="request.loading" />
              <template v-else>
                <template v-for="question in request.personalDetailsResponse.questions">
                  <QuestionAnswer :question="question" />
                </template>
              </template>
            </div>
          </div>
          <div
            v-if="!request.personalDetailsResponse && request.programMonth == 1"
            class="card stipend-request fit-content error-card"
          >
            <div class="card__section">
              <h2>Personal Details</h2>
              <p>No Personal Details survey submitted.</p>
            </div>
          </div>

          <!-- Goal Progression -->
          <div
            v-if="request.goalProgressionResponse"
            class="card stipend-request fit-content"
          >
            <div class="card__section">
              <h2>Goal Progression</h2>
              <LoadSpinner v-if="request.loading" />
              <template v-else>
                <template v-for="question in request.goalProgressionResponse.questions">
                  <QuestionAnswer :question="question" />
                </template>
              </template>
            </div>
          </div>

          <!-- Program Exit -->
          <div
            v-if="request.programExitResponse"
            class="card stipend-request fit-content"
          >
            <div class="card__section">
              <h2>Program Exit</h2>
              <LoadSpinner v-if="request.loading" />
              <template v-else>
                <template v-for="question in request.programExitResponse.questions">
                  <QuestionAnswer :question="question" />
                </template>
              </template>
            </div>
          </div>
          <!-- End Surveys Section -->


          <!-- Documentation Section -->
          <div
            v-if="request.documentsRef && request.documentsRef.length"
            class="card stipend-request fit-content"
          >
            <div class="card__section">
              <h2>Documentation</h2>
              <LoadSpinner v-if="request.loading" />
              <template v-else>
                <document-download
                  v-for="document in request.documentsRef"
                  :document="document"
                  :key="document.id"
                />
              </template>
            </div>
          </div>
          <div
            v-else
            class="card stipend-request fit-content error-card"
          >
            <div class="card__section">
              <h2>Documentation</h2>
              <p>No documents uploaded.</p>
            </div>
          </div>
        </div>
        <!-- End Documentation Section -->

      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import { DocumentDownload, LoadSpinner } from '@/components'
import FilePicker from '@/components/FilePicker'
import { formatNumberAsUSD } from '@/services/utils'

import CollectionManager from '@/services/collectionManager'

import Employee from '@/services/employees'
import EventCategory from '@/services/eventCategories'
import StipendRequest from '@/services/stipendRequests'
import Message from '@/services/messages'
import Staff from '@/services/staff'
import Document from '@/services/documents'

import EmployeeCard from '@/views/common/EmployeeCard'
import StipendUserCard from '@/views/common/StipendUserCard'
import MessageForm from '@/views/common/MessageForm'
import MessagesList from '@/views/common/MessagesList'
import AdminPermissions from '@/services/adminPermissions'
import QuestionAnswer from '@/components/survey/QuestionAnswer'

const EVENT_DETAIL_MODE = 'EVENT_DETAIL'
const MESSAGE_MODE = 'MESSAGE'

export default {
  name: 'StipendRequestDetail',
  components: {
    StipendUserCard,
    DocumentDownload,
    LoadSpinner,
    MessageForm,
    MessagesList,
    FilePicker,
    QuestionAnswer,
  },
  data() {
    return {
      StipendRequest,

      EVENT_DETAIL_MODE,
      MESSAGE_MODE,
      mode: EVENT_DETAIL_MODE,

      request: null,
      editableRequest: null,
      stipendRequest: null,
      employee: null,
      eventCategories: null,

      messages: CollectionManager.create({
        ModelClass: Message,
        filters: { ordering: '-datetime_created' },
      }),

      staff: CollectionManager.create({
        ModelClass: Staff,
      }),

      loading: false, // initial page load
      refreshing: false, // refreshing request data
      updating: false, // updating request status
      referring: false, // referring request
      updatingNotes: false,
      reportableEventNames: [],
    }
  },
  computed: {
    ...mapState(['user', 'organizationDetails']),
    availableStatusChoices() {
      // Remove certain statuses from the dropdown menu
      return StipendRequest.options.REQUEST_STATUS_CHOICES
    },

    showReferButton() {
      // For staff, only show button if request can be or has been referred.
      if (this.user.isStaff) {
        return this.request.canBeReferred

        // For admins, only show button if request has been referred.
      } else if (this.user.isAdmin) {
        return this.request.isReferred
      }
      return false
    },
    communicationName() {
      if (this.user.isStaff) {
        return 'Communication'
      }

      return 'Messages'
    },
    showMessages() {
      if (this.user.isStaff) {
        return true
      } else if (this.user.isAdmin) {
        return this.user.adminPermissions.canViewMessages
      }
      return false
    },
    showDocumentUpload() {
      if (this.user.isStaff) {
        return true
      } else if (this.user.isAdmin) {
        return this.user.adminPermissions.name === 'MASTER'
      }
      return false
    },
    legacyRequestQuestionsCheck() {
      return (
        (this.request &&
          this.request.eventsFinancialHardshipAnswer &&
          this.request.eventsFinancialHardshipAnswer != '') ||
        (this.request.eventsMisc && this.request.eventsMisc != '') ||
        (this.request.eventsUnexpectedOrUncontrollableMisc &&
          this.request.eventsUnexpectedOrUncontrollableMisc != '') ||
        (this.request.eventsBasicLivingDifficultiesMisc &&
          this.request.eventsBasicLivingDifficultiesMisc != '')
      )
    },
    isEditableAdmin() {
      if (
        this.user.isStaff ||
        (this.user.isAdmin &&
          this.user.adminPermissions.name == AdminPermissions.permissionNames.MASTER)
      ) {
        return true
      }
      return false
    },
  },
  created() {
    this.loading = true
    this.refresh().finally(() => {
      this.loading = false
      this.logAdminView()
    })
    const refreshEditing = this.refreshEditing
    const refreshMessages = this.refreshMessages
    this.refreshInterval = setInterval(function() {
      refreshEditing()
      refreshMessages()
    }, 15000)
    this.staff.refresh()
  },
  destroyed() {
    clearInterval(this.refreshInterval)
  },
  methods: {
    convertCatNameToId() {
      let eventCatIds = []
      if (this.eventCategories) {
        this.reportableEventNames.forEach(name => {
          const cat = this.eventCategories.find(cat => {
            return cat.name == name
          })
          if (cat) {
            eventCatIds.push(cat.id)
          }
        })
        if (eventCatIds.length == this.reportableEventNames.length) {
          this.editableRequest.reportableEventCategories = eventCatIds
        }
      }
    },
    getStatus(requestStatus) {
      const foundStatus = StipendRequest.options.REQUEST_STATUS_CHOICES.find(
        status => status.value === requestStatus,
      )

      return foundStatus && foundStatus.label
    },
    isReadOnlyAdmin() {
      if (
        this.user.isAdmin &&
        (
          this.user.adminPermissions.name === AdminPermissions.permissionNames.READ_ONLY
          || this.user.adminPermissions.name === AdminPermissions.permissionNames.TCR_ADMIN
        )
      ) {
        return true
      }

      return false
    },

    refresh() {
      this.refreshing = true
      const requestPromise = StipendRequest.api
        .retrieve(this.$route.params.id)
        .then(request => {
          this.request = request
          if(!request.declinedReason)
            request.declinedReason = StipendRequest.options.DOCUMENTATION_INSUFFICIENT_OR_UNCLEAR
          if(!request.declinedReasonDetail)
            request.declinedReasonDetail = ''
          this.editableRequest = Object.assign({}, request)
          this.refreshMessages()
          return request
        })
        .then(request => {
          return Employee.api.retrieve(request.userRef.id)
        })
        .then(employee => {
          this.employee = employee
        })

      // Refreshing is done once all of the above promises are done.
      return Promise.all([requestPromise]).finally(() => {
        this.refreshing = false
      })
    },
    refreshMessages() {
      if (this.request) {
        this.messages.filters.stipend_request = this.request.id
        return this.messages.refresh()
      }
    },
    refreshEditing() {
      if (this.request) {
        StipendRequest.api.retrieve(this.$route.params.id).then(request => {
          this.request.isEditing = request.isEditing
        })
      }
    },
    onMessageCreated(message) {
      this.refreshMessages()
    },
    onSelectReportableEventCategory() {
      this.updating = true
      this.convertCatNameToId()
      StipendRequest.api
        .update(this.editableRequest, ['reportableEventCategories'])
        .then(() => {
          this.request.reportableEventCategories = this.editableRequest.reportableEventCategories
        })
        .finally(() => {
          this.updating = false
        })
    },
    onSaveAdminNotes() {
      this.updatingNotes = true
      StipendRequest.api.update(this.request, ['adminNotes']).then(() => {
        this.refresh()
        this.updatingNotes = false
      })
    },
    updateStatus() {
      // Minimal validation
      if (
        this.editableRequest.status === StipendRequest.options.DECLINED &&
        !this.editableRequest.declinedReason
      ) {
        this.$Alert.alert({
          type: 'warning',
          message: `
            <h4>Error</h4>
            <p>Please select the reason why you are declining this request.</p>
          `,
          timeout: 5000,
        })
      }

      if (this.editableRequest.amount < 0) {
        this.$Alert.alert({
          type: 'warning',
          message: `
            <h4>Error</h4>
            <p>You may not reimburse a negative amount. Please enter a
               positive number and try again.</p>
          `,
          timeout: 5000,
        })
        return
      }

      if (this.editableRequest.amount > this.request.amount) {
        this.$Alert.alert({
          type: 'warning',
          message: `
            <h4>Error</h4>
            <p>You may not reimburse an amount greater than the requested amount.
               Please update it and try again.</p>
          `,
          timeout: 5000,
        })
        return
      }

      if (this.organizationDetails.grantLimit) {
        if (
          this.editableRequest.amount + this.user.totalPayoutsSinceLastGrantLimitReset >
          this.organizationDetails.grantLimit
        ) {
          let max_amount =
            this.organizationDetails.grantLimit - this.user.totalPayoutsSinceLastGrantLimitReset
          let max_amount_formatted = formatNumberAsUSD(max_amount)
          this.$Alert.alert({
            type: 'warning',
            message: `
              <h4>Error</h4>
              <p>You may not reimburse an amount greater than the grant limit.
                Please update it and try again. (Maximum: ${max_amount_formatted})</p>
            `,
            timeout: 5000,
          })
          this.editableRequest.amount = max_amount
          return
        }
      }

      this.updating = true
      StipendRequest.api
        .updateStatus(this.editableRequest)
        .then(() => {
          this.refresh()

          // If user can send messages, direct them to send one after updating status
          if (this.showMessages) {
            this.mode = MESSAGE_MODE
            this.$Alert.alert({
              type: 'success',
              message: `
              <h4>Status updated!</h4>
              <p>Employees are <em>not</em> automatically notified, so you may want
                 to write them a message.</p>
            `,
              timeout: 6000,
            })
          } else {
            this.$Alert.alert({
              type: 'success',
              message: `
              <h4>Status updated!</h4>
              <p>Note: Employees are not automatically notified.</p>
            `,
              timeout: 6000,
            })
          }
        })
        .finally(() => {
          this.updating = false
        })
    },
    markAllMessagesRead() {
      // Mark all unread messages as "read" when the user
      // switches to the 'Messages' view.
      this.messages.list
        .filter(m => m.isUnread)
        .forEach(m => {
          m.isUnread = false
          Message.api.markRead(m).catch(() => {
            m.isUnread = true
          })
        })
    },
    onReferRequest() {
      this.referring = true
      StipendRequest.api
        .refer(this.editableRequest)
        .then(() => {
          this.refresh()
          this.$Alert.alert({
            type: 'success',
            message: `<h4>Request referred!</h4>`,
            timeout: 5000,
          })
        })
        .finally(() => {
          this.referring = false
        })
    },
    async logAdminView() {
      await StipendRequest.api.logAdminView(this.request)
    },
    onUpdateAssigned() {
      StipendRequest.api.update(this.request, ['assignedTo']).then(() => {
        this.refresh()
      })
    },
    onResetAssigned() {
      this.request.assignedTo = ''
      this.onUpdateAssigned()
    },
    onEventDocumentUpload(event, file) {
      const doc = Document.create({
        file: file,
        filename: file.name,
      })
      doc.event = event.id
      Document.api.create(doc).then(document => event.documentsRef.push(document))
    },
    onExpenseDocumentUpload(expense, file) {
      const doc = Document.create({
        file: file,
        filename: file.name,
      })
      doc.expense = expense.id
      Document.api.create(doc).then(document => expense.documentsRef.push(document))
    },
  },
  watch: {
    mode(newVal) {
      if (newVal === MESSAGE_MODE) {
        this.markAllMessagesRead()
      }
    },
    request() {
      EventCategory.api.list({
        filters: { event_category_organization: this.request.organization },
        }).then(eventCategories => {
          this.eventCategories = eventCategories.results
        }).then(() => {
          this.reportableEventNames = this.eventCatNames
        })
    },
    'request.followUpDate': {
      async handler(newFollowUpDate, oldFollowUpDate) {
        if (!this.isEditableAdmin) return
        if (this.request == null) return
        if (!newFollowUpDate || !oldFollowUpDate) return
        if (new Date(newFollowUpDate).getTime() == new Date(oldFollowUpDate).getTime()) return
        if (this.request.followUpDate == 'undefined') return

        if (newFollowUpDate) {
          newFollowUpDate = newFollowUpDate.toISOString()
        }

        await StipendRequest.api
          .update({ followUpDate: newFollowUpDate, id: this.request.id }, [
            'followUpDate',
          ])
          .catch(error => {
            this.request.followUpDate = oldFollowUpDate
            throw error
          })
      },
    },
    editableRequest: {
      handler(newVal, oldVal) {
        if (oldVal === null || newVal === null) return
        this.editableRequest.hasEdit = true

        // Reset the amount on the editable request if the
        // user changes the status away from 'Partially Approved'
        // or 'Partially Approved Pending'.
        if (
          (oldVal.status === StipendRequest.options.PARTIALLY_APPROVED ||
            oldVal.status === StipendRequest.options.PARTIALLY_APPROVED_PENDING) &&
          newVal.status !== StipendRequest.options.PARTIALLY_APPROVED &&
          newVal.status !== StipendRequest.options.PARTIALLY_APPROVED_PENDING
        ) {
          this.editableRequest.amount = this.request.amount
        }

        // Reset the declined reason on the editable request if the
        // user changes the status away from 'Declined'.
        if (
          oldVal.status === StipendRequest.options.DECLINED &&
          newVal.status !== StipendRequest.options.DECLINED
        ) {
          this.editableRequest.declinedReason = this.request.declinedReason
        }

        this.editableRequest.payoutType = StipendRequest.options.PAYPAL
      },
      deep: true,
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../../styles/mixins/containers';
@import '@/styles/variables';
@import '../../styles/mixins/cards';

.card {
  @include card;
}

.stipend-request {
  margin-top: 2rem;

  .question {
    margin: 1rem 0rem;

    .question-text {
      border-bottom: 1px solid $soft-gray;
    }

    p {
      margin-top: 0.3rem;
    }
  }
}

.title-block {
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 2.5rem;
}
.title {
  padding-right: 1rem;
  margin-bottom: 0rem;
  // ^For some reason this h2 tag has a margin-bottom unless I explicitly remove it
}
.staff-request-detail {
  @include screen-container;
}

.container {
  @include flex-container;
}

.request-content {
  flex: 12;

  &__section {
    padding: 1rem;

    &--bordered {
      padding-top: 2rem;
      border-top: 1px solid $soft-gray;
    }

    &-top {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
  }
}

.reportable-categories {
  margin-bottom: -1.5rem;
}

.fit-content {
  height: fit-content;
}

// HACK: The material design package or theme does not
//       have working styles by default. For instance,
//       the pop-up menu background is transparent!
// ^ Dumb, didn't understand the docs...
.md-field > label {
  font-weight: bold;
  color: $base-gray;
}

.md-field {
  max-width: 26rem;
}
.md-field .md-input {
  margin-right: 1rem;
}

.md-field .md-textarea {
  max-height: 35rem;
  font-size: 14px;
  line-height: 1rem;
}

.md-menu.md-select {
  padding: 0.25rem 0.5rem;
  margin-right: 1rem;
}

.admin-notes-container {
  max-width: 24rem;
  margin-bottom: 0;

  &__btn-container {
    display: flex;
    justify-content: space-between;
  }
}
#admin-notes-text {
  min-height: 12rem;
}

// END HACK

.sidebar {
  padding: 0 1rem 0 0;
}

.editing-alert {
  display: flex;
  flex-direction: row;
  align-items: center;
  background-color: #fff3cd;
  justify-content: flex-start;
  width: 100%;
  padding: 1rem;
  margin: 1rem 0;
  border-radius: 8px;

  &__icon {
    margin: 4px;
  }
}

.file-picker {
  width: 10rem;
}

.error-card, .no-answer-submitted {
  background: #faeae8;
}

.at-risk {
  color: red;
}
</style>
